import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { BLANK_QUOTE_RATES, BlankQuoteNotice, LeaveBlankPerQuote, applyLeaveBlank, getBlankQuotesForPlan, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const TIERS = [
    { key: 'prestige', label: 'Prestige' },
    { key: 'elite', label: 'Elite' },
    { key: 'executive', label: 'Executive' },
];

// Inpatient is always included and can't be unchecked — same as International Inpatient in
// Cigna Global Health Option. The other 5 benefits are independently optional, each with its
// own "type" sub-option, and the field name is always `${tier}_${key}` regardless of tier.
const CATEGORIES = [
    { key: 'inpatient', label: 'Inpatient', optionsKey: 'inpatient_options' },
    { key: 'outpatient', label: 'Outpatient', optionsKey: 'outpatient_options' },
    { key: 'maternity', label: 'Maternity', optionsKey: 'maternity_options' },
    { key: 'preventive', label: 'Preventive', optionsKey: 'preventive_options' },
    { key: 'dental', label: 'Dental', optionsKey: 'dental_options' },
    { key: 'vision', label: 'Vision', optionsKey: 'vision_options' },
];

const MANDATORY_CATEGORY_KEY = 'inpatient';
const RIDERS = CATEGORIES.filter(({ key }) => key !== MANDATORY_CATEGORY_KEY);

// Every tier × category field the API returns — all are kept so switching tier needs no refetch
const RATE_FIELDS = TIERS.flatMap(t => CATEGORIES.map(c => `${t.key}_${c.key}`));

// CignaCareConnect is SGD-only — CignaCareConnect_Rates.js hardcodes this locally
// (`["SGD"]`) rather than reading appConfig.content.currencies.
const CURRENCIES = ['SGD'];
const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];
const BRAND_COLOR = '#00629b';
const TIER_ROW_COLOR = '#b9f6ca';
const TOTAL_ROW_COLOR = '#dfe3ec';

function formatRate(rate) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return 'S$' + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as CignaCareConnect_Rates: discount/loading scale every category, GST is added on the total
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function CignaCareConnect_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const areaOptions = appConfig?.content?.cigna_care_connect_area_of_coverage || [];
    const defaultArea = areaOptions.includes('Singapore') ? 'Singapore' : (areaOptions[0] || '');
    const gst = appConfig?.content?.specifics?.cigna_care_connect?.gst ?? 0;

    // CignaCareConnect_Rates.js carries a deductible default in Quotation1.js's shared state
    // (appConfig.content.deductibles.cigna_care_connect['SGD'][0]) even though it has no
    // deductible selector in its own UI and never reads it for rate computation — it's just
    // always present in the request body. This dialog reproduces that fixed value silently
    // rather than surfacing a selector that wouldn't do anything.
    const fixedDeductible = appConfig?.content?.deductibles?.cigna_care_connect?.['SGD']?.[0] || '';

    function getCategoryOptions(category) {
        return appConfig?.content?.specifics?.cigna_care_connect?.[category.optionsKey] || [];
    }

    const [tier, setTier] = useState('prestige');
    const [selectedCategories, setSelectedCategories] = useState(new Set([MANDATORY_CATEGORY_KEY]));
    const [categoryTypes, setCategoryTypes] = useState(() => {
        const initial = {};
        CATEGORIES.forEach(c => { initial[c.key] = ''; });
        return initial;
    });
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency] = useState('SGD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    // { [quoteUid]: boolean } — quotes whose premiums are left blank
    const [blankQuotes, setBlankQuotes] = useState({});
    const [loading, setLoading] = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    // Default each category's "type" to its first configured option, once options load
    useEffect(() => {
        if (initializingFromEdit.current) return;
        setCategoryTypes(prev => {
            const next = { ...prev };
            let changed = false;
            CATEGORIES.forEach(c => {
                if (!next[c.key]) {
                    const firstOption = getCategoryOptions(c)[0];
                    if (firstOption) { next[c.key] = firstOption; changed = true; }
                }
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appConfig]);

    const selectedTierObj = TIERS.find(t => t.key === tier) || TIERS[0];
    const activeRiders = RIDERS.filter(r => selectedCategories.has(r.key));
    const activeCategories = [CATEGORIES.find(c => c.key === MANDATORY_CATEGORY_KEY), ...activeRiders];

    // Pre-populate form when opening in edit mode
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            const savedTier = editingPlan.plan || (editingPlan.plan_id || '').replace('cigna_care_connect_', '') || 'prestige';
            setTier(savedTier);
            // Force-include the mandatory category even for plans saved before it became mandatory.
            setSelectedCategories(new Set([MANDATORY_CATEGORY_KEY, ...(editingPlan.payloads?.categories || [])]));
            setCategoryTypes(editingPlan.payloads?.category_types || (() => {
                const initial = {};
                CATEGORIES.forEach(c => { initial[c.key] = getCategoryOptions(c)[0] || ''; });
                return initial;
            })());
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || 'SGD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (typeof val === 'number' && savedFactor > 0 ? val / savedFactor : val);

            // Reconstruct ratesData from stored per-category premiums — only the saved tier's
            // fields are recoverable
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    return Object.fromEntries(CATEGORIES.map(c => [`${savedTier}_${c.key}`, unscale(premium?.[`${c.key}_premium`] ?? null)]));
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open) {
            setTier('prestige');
            setSelectedCategories(new Set([MANDATORY_CATEGORY_KEY]));
            setAreaOfCoverage(defaultArea);
            setCurrency('SGD');
            setPaymentFrequency('Annually');
            setDiscount('0');
            setLoadingPercent('0');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (open) setBlankQuotes(getBlankQuotesForPlan(quotes, quotations, editingPlan));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Every category's rate comes back in one fetch, so toggling a benefit just shows a
    // different set of rows — no refresh needed.
    function toggleCategory(key) {
        if (key === MANDATORY_CATEGORY_KEY) return;
        setSelectedCategories(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return !!blankQuotes[quote.uid];
    }

    async function handleViewRates() {
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { cigna_care_connect: fixedDeductible },
                            insurers: ['cigna_care_connect'],
                            specifics: {
                                cigna_care_connect: Object.fromEntries(
                                    CATEGORIES.map(c => [c.key, { type: categoryTypes[c.key] }])
                                ),
                            },
                            specific_area_of_coverage: { cigna_care_connect: areaOfCoverage },
                            rates_version_per_insurer: { cigna_care_connect: selectedRatesVersion?.cigna_care_connect || '' },
                            specific_currency: { cigna_care_connect: currency },
                            specific_payment_frequency: { cigna_care_connect: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.cigna_care_connect }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(RATE_FIELDS.map(f => [f, r[f]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Cigna Care Connect rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // Category rate for the selected tier with discount/loading applied (GST-exclusive);
    // ratesData keeps the raw API rates
    function getCategoryRate(clientIndex, categoryKey, quoteUid) {
        const val = ratesData?.[quoteUid]?.rawRates?.[clientIndex]?.[`${tier}_${categoryKey}`];
        if (val === undefined || val === null) return null;
        return typeof val === 'number' ? val * rateFactor : val;
    }

    // Matches computeTotalPremiumPerClient: sum of active categories, then GST applied once
    function getTotal(clientIndex, quoteUid) {
        let total = 0;
        let hasAny = false;
        activeCategories.forEach(c => {
            const rate = getCategoryRate(clientIndex, c.key, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        });
        return hasAny ? total * (1 + gst) : null;
    }

    function sumClients(getter) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getter(i);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function buildPlanLabel() {
        if (activeRiders.length === 0) return selectedTierObj.label;
        return `${selectedTierObj.label} + ${activeRiders.map(r => r.label).join(' + ')}`;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getTotal(clientIndex, quote.uid);

                // Category premiums are stored GST-exclusive with discount/loading applied
                const premium = {
                    insurer: 'cigna_care_connect',
                    plan: tier,
                    plan_label: planLabel,
                    included_benefits: '',
                    deductible: '',
                    area_of_coverage: areaOfCoverage,
                    currency,
                    payment_frequency: paymentFrequency,
                    gst,
                    discount,
                    loading: loadingPercent,
                    premium_rate: rate ?? null,
                };

                CATEGORIES.forEach(c => {
                    premium[`${c.key}_premium`] = selectedCategories.has(c.key) ? getCategoryRate(clientIndex, c.key, quote.uid) : null;
                });

                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: `cigna_care_connect_${tier}`,
                    planUid,
                    premium,
                });
            });
        });

        const plan = {
            plan_id: `cigna_care_connect_${tier}`,
            uid: planUid,
            plan: tier,
            insurer: 'cigna_care_connect',
            label: `Cigna Care Connect ${planLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                categories: [...selectedCategories],
                category_types: { ...categoryTypes },
                discount,
                loading: loadingPercent,
            },
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: BRAND_COLOR,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    height: 60,
                }}>Cigna Care Connect</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', background: '#f8f9fa', px: 1.5, py: 0.5 }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/cigna-logo.svg`}
                            alt="Cigna"
                            style={{ height: 55, userSelect: 'none', WebkitUserDrag: 'none' }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={tier}
                            disabled={loading || !!editingPlan}
                            // Rates for every tier come back in one fetch, so switching tiers just shows different rows
                            onChange={(e) => setTier(e.target.value)}
                            sx={selectSx}
                        >
                            {TIERS.map(t => (
                                <MenuItem key={t.key} value={t.key} sx={menuItemSx}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Benefits — Inpatient is always included; the rest are optional, each with its own type sub-option */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Benefits</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {CATEGORIES.map(category => {
                            const isMandatory = category.key === MANDATORY_CATEGORY_KEY;
                            return (
                            <Box key={category.key} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    sx={{
                                        mb: 0,
                                        '& .MuiFormControlLabel-label': { fontSize: 13, width: 100 },
                                        ...(isMandatory && {
                                            '& .MuiFormControlLabel-label.Mui-disabled': { color: 'text.primary' },
                                        }),
                                    }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={isMandatory ? true : selectedCategories.has(category.key)}
                                            onChange={() => toggleCategory(category.key)}
                                            disabled={isMandatory || loading || !!editingPlan}
                                            sx={isMandatory ? { '&.Mui-disabled': { color: 'primary.main' } } : undefined}
                                        />
                                    }
                                    label={category.label}
                                />
                                <Select
                                    size="small"
                                    value={categoryTypes[category.key] || ''}
                                    // The type is part of the rates request, so changing it invalidates the shown rates
                                    onChange={(e) => { setCategoryTypes(prev => ({ ...prev, [category.key]: e.target.value })); invalidateRates(); }}
                                    sx={{ ...selectSx, width: 160 }}
                                    disabled={loading}
                                >
                                    {getCategoryOptions(category).map(option => (
                                        <MenuItem key={option} value={option} sx={menuItemSx}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area of Coverage</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={areaOfCoverage} disabled={loading} onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {areaOptions.map(area => <MenuItem key={area} value={area} sx={menuItemSx}>{area}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={currency} disabled={loading} onChange={(e) => { setCurrency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {CURRENCIES.map(c => <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={paymentFrequency} disabled={loading} onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }} sx={selectSx}>
                                {PAYMENT_FREQUENCIES.map(f => <MenuItem key={f} value={f} sx={menuItemSx}>{f}</MenuItem>)}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <LeaveBlankPerQuote
                    quotes={quotes}
                    blankQuotes={blankQuotes}
                    onChange={(next) => { setBlankQuotes(next); invalidateRates(); }}
                    disabled={loading}
                />

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: '#673ab7' }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>{quote.name}</Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 260 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Tier heading — same as CignaCareConnect_Rates */}
                                            <TableRow>
                                                <TableCell colSpan={clients.length + 2} sx={{ fontWeight: 600, background: TIER_ROW_COLOR, color: '#000' }}>{selectedTierObj.label}</TableCell>
                                            </TableRow>
                                            {activeCategories.map((category, categoryIdx) => (
                                                <TableRow key={category.key} sx={{ backgroundColor: categoryIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{category.label}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getCategoryRate(idx, category.key, quote.uid))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(sumClients(i => getCategoryRate(i, category.key, quote.uid)))}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow sx={{ backgroundColor: TOTAL_ROW_COLOR }}>
                                                <TableCell sx={{ fontWeight: 700 }}>Total Premium Incl. GST {gst * 100}%</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                        {formatRate(getTotal(idx, quote.uid))}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    {formatRate(sumClients(i => getTotal(i, quote.uid)))}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="cigna_care_connect" />

                <RatesVersionSelect
                    insurer="cigna_care_connect"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
